/**
 * Componente Modal
 *
 * Diálogo modal reutilizável com animações.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { isTopModal, pushModal } from '@/utils/modalStack';
import { lockBodyScroll } from '@/utils/scrollLock';
import { X } from 'lucide-react';

/**
 * Tamanhos do modal
 */
type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/**
 * Props do componente Modal
 */
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: ModalSize;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Classe de z-index customizada (ex: 'z-[110]'). Padrão: 'z-50' */
  zIndex?: string;
}

/**
 * Classes CSS por tamanho
 */
const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  full: 'max-w-4xl',
};

/**
 * Componente Modal
 */
export function Modal({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  children,
  footer,
  zIndex = 'z-50',
}: ModalProps) {
  // Posição na pilha de modais: com modais empilhados, só o do topo responde ao Esc
  const stackIdRef = useRef<symbol | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    const entry = pushModal();
    stackIdRef.current = entry.id;
    // Trava a rolagem preservando o valor anterior (modais empilhados)
    const unlock = lockBodyScroll();
    return () => {
      entry.release();
      stackIdRef.current = null;
      unlock();
    };
  }, [isOpen]);

  // Fecha modal ao pressionar Escape (apenas o modal do topo)
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const id = stackIdRef.current;
      if (id && !isTopModal(id)) return;
      onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  return (
    <Fragment>
      {/* Overlay */}
      <div
        className={`fixed inset-0 ${zIndex} bg-black/50 backdrop-blur-sm transition-opacity`}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className={`fixed inset-0 ${zIndex} flex items-center justify-center p-2 sm:p-4`}>
        <div
          className={`relative flex w-full ${sizeClasses[size]} max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] transform flex-col rounded-xl bg-white shadow-xl transition-all dark:bg-gray-800`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          aria-describedby={description ? 'modal-description' : undefined}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex shrink-0 items-start justify-between border-b border-gray-200 p-4 sm:p-6 dark:border-gray-700">
              <div>
                {title && (
                  <h2
                    id="modal-title"
                    className="text-lg font-semibold text-primary-900 sm:text-xl dark:text-white"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id="modal-description"
                    className="mt-1 text-sm text-gray-500 dark:text-gray-400"
                  >
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  aria-label="Fechar"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-200 px-4 py-4 sm:px-6 dark:border-gray-700">
              {footer}
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}

/**
 * Hook para controlar modal
 */
export function useModal(initialState = false) {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(prev => !prev);

  return { isOpen, open, close, toggle };
}
